import * as React from 'react';
import { PinnacleTabs } from '../../../../components/PinnacleTabs/PinnacleTabs';
import { Program } from '../../../../api/interfaces/Program';
import { Box } from '@mui/material';
import { pushNotification } from '../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../../../utils/errorHandler';
import ReferenceInformation from './tabs/ReferenceInformation';
import { FormData } from '../../hooks/useReferenceForm';
import ContentManagement from './tabs/ContentManagement';
import AdditionalSettings from './tabs/AdditionalSettings';

interface CreateReferenceDrawerContentProps {
  formData: FormData;
  rawFormData: FormData;
  touchedFields: Record<keyof FormData, boolean>;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur: (field: keyof FormData) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  programs: Program[];
  isFormValid: () => boolean;
  handleUploadClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedImage: File | null | any;

  closeToggle: () => void;
  onReferencesCallback?: (programId: string) => void;
}

export interface CreateReferenceDrawerContentHandle {
  handleSubmit: () => void;
}

const CreateReferenceDrawerContent = React.forwardRef<CreateReferenceDrawerContentHandle, CreateReferenceDrawerContentProps>(
  ({
    rawFormData,
    formData,
    touchedFields,
    onChange,
    onBlur,
    onSubmit,
    isSubmitting,
    programs,
    isFormValid,
    handleUploadClick,
    fileInputRef,
    handleFileChange,
    selectedImage,
    setIsSubmitting,
  }, ref) => {
    React.useImperativeHandle(ref, () => ({
      handleSubmit: async () => {
        setIsSubmitting(true);

        try {
          if (isFormValid()) {
            await onSubmit();
          } else {
            pushNotification({
              details: 'Some required fields are missing.',
              type: NotificationTypes.Error,
            });
          }
        } catch (error) {
          handleApiError(error, {
            notify: (msg) => {
              pushNotification({
                details: msg,
                type: NotificationTypes.Error,
              });
            },
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    }));

    return (
      <Box display="flex" flexDirection="column" height="100%">
        <PinnacleTabs
          disabled={isSubmitting}
          tabs={[
            {
              label: "Reference Information",
              content: (
                <ReferenceInformation
                  programs={programs}
                  formData={rawFormData}
                  touchedFields={touchedFields}
                  onChange={onChange}
                  onBlur={onBlur}
                  isSubmitting={isSubmitting}
                  handleUploadClick={handleUploadClick}
                  fileInputRef={fileInputRef}
                  handleFileChange={handleFileChange}
                  selectedImage={selectedImage}
                />
              ),
            },
            {
              label: "Reference Content",
              content: (
                <ContentManagement
                  programs={programs}
                  formData={rawFormData}
                  touchedFields={touchedFields}
                  onChange={onChange}
                  onBlur={onBlur}
                  isSubmitting={isSubmitting}
                />
              ),
            },
            {
              label: "Additional Settings",
              content: (
                <AdditionalSettings
                  formData={rawFormData}
                  onChange={onChange}
                  isSubmitting={isSubmitting}
                />
              ),
              disabledTabItem: false
            },
          ]}
        />
      </Box>
    );
  }
);


export default CreateReferenceDrawerContent;
